// wait for Yjs updates to quiesce before binding ProseMirror
const CONTENT_SETTLE_MS = 400;
const EMPTY_DOC_WAIT_MS = 5000;
const FORCE_HIDE_AFTER_MS = 30000;

export function setupLoadingStateManager({ydoc, provider, setIsLoading, setLoadingStage, onSynced, getView}) {
    let loadingHidden = false;
    let editorInitRequested = false;
    let emptyDocCheckTimeout = null;
    let settleTimeout = null;
    let forceHideTimeout = null;
    let destroyed = false;
    let connected = false;
    let lastFragmentLength = -1;

    const getFragmentLength = () => ydoc.getXmlFragment("prosemirror").length;

    const hideLoading = () => {
        if (destroyed || loadingHidden) return;
        loadingHidden = true;
        setIsLoading(false);
    };

    const hideLoadingWhenViewReady = () => {
        if (destroyed || loadingHidden) return;
        const view = getView();
        if (!view) return;
        hideLoading();
    };

    const requestEditorInit = () => {
        if (destroyed || editorInitRequested) return;
        editorInitRequested = true;
        if (onSynced) onSynced(true);
        requestAnimationFrame(hideLoadingWhenViewReady);
    };

    const clearSettleTimeout = () => {
        if (settleTimeout) {
            clearTimeout(settleTimeout);
            settleTimeout = null;
        }
    };

    const scheduleContentSettled = () => {
        if (destroyed || editorInitRequested) return;

        clearSettleTimeout();
        settleTimeout = setTimeout(() => {
            settleTimeout = null;
            if (destroyed || editorInitRequested) return;

            const length = getFragmentLength();
            if (length === 0) return;

            if (length !== lastFragmentLength) {
                lastFragmentLength = length;
                scheduleContentSettled();
                return;
            }

            if (!provider.synced) {
                scheduleContentSettled();
                return;
            }

            requestEditorInit();
        }, CONTENT_SETTLE_MS);
    };

    const onFragmentLengthChanged = () => {
        if (destroyed) return;

        const length = getFragmentLength();
        if (length === lastFragmentLength) return;

        lastFragmentLength = length;

        if (length > 0) {
            if (emptyDocCheckTimeout) {
                clearTimeout(emptyDocCheckTimeout);
                emptyDocCheckTimeout = null;
            }
            if (provider.synced) scheduleContentSettled();
        }
    };

    const startEmptyDocWait = () => {
        if (emptyDocCheckTimeout || editorInitRequested) return;

        emptyDocCheckTimeout = setTimeout(() => {
            emptyDocCheckTimeout = null;
            if (destroyed || editorInitRequested) return;

            if (getFragmentLength() === 0 && provider.synced) {
                requestEditorInit();
                hideLoading();
            }
        }, EMPTY_DOC_WAIT_MS);
    };

    const handleSynced = (synced) => {
        if (!synced) return;

        const length = getFragmentLength();
        lastFragmentLength = length;

        if (length > 0) {
            scheduleContentSettled();
        } else {
            startEmptyDocWait();
        }
    };

    const handleStatus = (event) => {
        if (event.status === 'connected' && !connected) {
            connected = true;
            if (setLoadingStage) setLoadingStage('syncing');
            if (provider.synced) handleSynced(true);
        } else if (event.status === 'disconnected') {
            connected = false;
            if (setLoadingStage) setLoadingStage('connecting');
        }
    };

    const handleYDocUpdate = () => {
        onFragmentLengthChanged();
        hideLoadingWhenViewReady();
    };

    provider.on("synced", handleSynced);
    provider.on("status", handleStatus);
    ydoc.on("update", handleYDocUpdate);

    if (provider.synced) {
        handleSynced(true);
    }

    forceHideTimeout = setTimeout(() => {
        if (!destroyed && !loadingHidden) {
            console.warn("[editor] force-hiding loading overlay after timeout");
            if (!editorInitRequested) requestEditorInit();
            hideLoading();
        }
    }, FORCE_HIDE_AFTER_MS);

    const cleanup = () => {
        destroyed = true;
        provider.off("synced", handleSynced);
        provider.off("status", handleStatus);
        ydoc.off("update", handleYDocUpdate);
        clearSettleTimeout();
        if (emptyDocCheckTimeout) clearTimeout(emptyDocCheckTimeout);
        if (forceHideTimeout) clearTimeout(forceHideTimeout);
    };

    return {
        hideLoadingWhenViewReady,
        cleanup,
    };
}
